import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { logger } from '@/utils/logger';
import { useCustomers, useUpdateCustomerCell, useCreateCustomer, useDeleteCustomer } from '@/hooks/useVendorCustomer';
import { ICustomer } from '@/types/psi';
import { MobileCard } from '@/components/ui';

// Column configuration matching the 國內客戶 sheet (28 columns A-AB)
const COLUMNS = [
  { key: 'customer_id', labelKey: 'customer.columns.customerId', column: 'A', width: 'w-20' },
  { key: 'level', labelKey: 'customer.columns.level', column: 'B', width: 'w-16' },
  { key: 'chinese_name', labelKey: 'customer.columns.chineseName', column: 'C', width: 'w-32' },
  { key: 'tax_id', labelKey: 'customer.columns.taxId', column: 'D', width: 'w-24' },
  { key: 'product_type', labelKey: 'customer.columns.productType', column: 'E', width: 'w-24' },
  { key: 'main_product', labelKey: 'customer.columns.mainProduct', column: 'F', width: 'w-28' },
  { key: 'production_vol', labelKey: 'customer.columns.productionVol', column: 'G', width: 'w-20' },
  { key: 'phone', labelKey: 'customer.columns.phone', column: 'H', width: 'w-28' },
  { key: 'fax', labelKey: 'customer.columns.fax', column: 'I', width: 'w-28' },
  { key: 'website', labelKey: 'customer.columns.website', column: 'J', width: 'w-32' },
  { key: 'shipping_terms', labelKey: 'customer.columns.shippingTerms', column: 'K', width: 'w-24' },
  { key: 'currency', labelKey: 'customer.columns.currency', column: 'L', width: 'w-20' },
  { key: 'payment_method', labelKey: 'customer.columns.paymentMethod', column: 'M', width: 'w-24' },
  { key: 'payment_terms', labelKey: 'customer.columns.paymentTerms', column: 'N', width: 'w-24' },
  { key: 'payment_bank', labelKey: 'customer.columns.paymentBank', column: 'O', width: 'w-28' },
  { key: 'payment_account', labelKey: 'customer.columns.paymentAccount', column: 'P', width: 'w-28' },
  { key: 'company_address', labelKey: 'customer.columns.companyAddress', column: 'Q', width: 'w-40' },
  { key: 'factory_address', labelKey: 'customer.columns.factoryAddress', column: 'R', width: 'w-40' },
  { key: 'contact1', labelKey: 'customer.columns.contact1', column: 'S', width: 'w-20' },
  { key: 'extension1', labelKey: 'customer.columns.extension1', column: 'T', width: 'w-16' },
  { key: 'mobile1', labelKey: 'customer.columns.mobile1', column: 'U', width: 'w-28' },
  { key: 'email1', labelKey: 'customer.columns.email1', column: 'V', width: 'w-36' },
  { key: 'contact2', labelKey: 'customer.columns.contact2', column: 'W', width: 'w-20' },
  { key: 'extension2', labelKey: 'customer.columns.extension2', column: 'X', width: 'w-16' },
  { key: 'mobile2', labelKey: 'customer.columns.mobile2', column: 'Y', width: 'w-28' },
  { key: 'email2', labelKey: 'customer.columns.email2', column: 'Z', width: 'w-36' },
  { key: 'account_manager', labelKey: 'customer.columns.accountManager', column: 'AA', width: 'w-24' },
  { key: 'status', labelKey: 'customer.columns.status', column: 'AB', width: 'w-24' },
];

interface EditableCellProps {
  value: string;
  rowIndex: number;
  column: string;
  onUpdate: (rowIndex: number, column: string, value: string) => void;
  isUpdating: boolean;
}

const EditableCell: React.FC<EditableCellProps> = ({ value, rowIndex, column, onUpdate, isUpdating }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);

    // Debounce the update
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      if (newValue !== value) {
        onUpdate(rowIndex, column, newValue);
      }
    }, 1000); // 1 second debounce
  };

  const handleBlur = () => {
    setIsEditing(false);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    if (localValue !== value) {
      onUpdate(rowIndex, column, localValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleBlur();
    }
    if (e.key === 'Escape') {
      setLocalValue(value);
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className="w-full px-2 py-1 border border-indigo-500 dark:border-indigo-400 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
      />
    );
  }

  return (
    <div
      onClick={() => setIsEditing(true)}
      className={`px-2 py-1 cursor-pointer hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded min-h-[28px] text-sm truncate text-gray-900 dark:text-white ${
        isUpdating ? 'bg-yellow-50 dark:bg-yellow-900/30 animate-pulse' : ''
      }`}
      title={localValue || 'Click to edit'}
    >
      {localValue || <span className="text-gray-400 dark:text-gray-500 italic">-</span>}
    </div>
  );
};

const CustomerList: React.FC = () => {
  const { t } = useTranslation('psi');
  const { data: customers, isLoading, error, refetch } = useCustomers();
  const updateCell = useUpdateCustomerCell();
  const createCustomer = useCreateCustomer();
  const deleteCustomer = useDeleteCustomer();

  const [updatingCells, setUpdatingCells] = useState<Set<string>>(new Set());
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    customer_id: '',
    level: '',
    chinese_name: '',
    tax_id: '',
    product_type: '',
    main_product: '',
    production_vol: '',
    phone: '',
    fax: '',
    website: '',
    shipping_terms: '',
    currency: '',
    payment_method: '',
    payment_terms: '',
    payment_bank: '',
    payment_account: '',
    company_address: '',
    factory_address: '',
    contact1: '',
    extension1: '',
    mobile1: '',
    email1: '',
    contact2: '',
    extension2: '',
    mobile2: '',
    email2: '',
    account_manager: '',
    status: '',
  });

  const handleCellUpdate = useCallback(async (rowIndex: number, column: string, value: string) => {
    const cellKey = `${rowIndex}-${column}`;
    setUpdatingCells(prev => new Set(prev).add(cellKey));

    try {
      await updateCell.mutateAsync({ row_index: rowIndex, column, value });
    } catch (error) {
      logger.error('Failed to update cell:', error);
    } finally {
      setUpdatingCells(prev => {
        const next = new Set(prev);
        next.delete(cellKey);
        return next;
      });
    }
  }, [updateCell]);

  const handleAddCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createCustomer.mutateAsync(newCustomer);
      setNewCustomer({
        customer_id: '',
        level: '',
        chinese_name: '',
        tax_id: '',
        product_type: '',
        main_product: '',
        production_vol: '',
        phone: '',
        fax: '',
        website: '',
        shipping_terms: '',
        currency: '',
        payment_method: '',
        payment_terms: '',
        payment_bank: '',
        payment_account: '',
        company_address: '',
        factory_address: '',
        contact1: '',
        extension1: '',
        mobile1: '',
        email1: '',
        contact2: '',
        extension2: '',
        mobile2: '',
        email2: '',
        account_manager: '',
        status: '',
      });
      setShowAddForm(false);
    } catch (error) {
      logger.error('Failed to create customer:', error);
    }
  };

  const handleDeleteCustomer = async (rowIndex: number) => {
    if (window.confirm(t('customer.confirmDelete'))) {
      try {
        await deleteCustomer.mutateAsync(rowIndex);
      } catch (error) {
        logger.error('Failed to delete customer:', error);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <svg className="animate-spin h-8 w-8 text-indigo-600" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        <span className="ml-3 text-gray-600 dark:text-gray-400">{t('customer.loading')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-700">{t('customer.loadError')}</p>
        <button onClick={() => refetch()} className="mt-2 text-red-600 underline">
          {t('common.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('customer.title')}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('customer.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => refetch()}
            className="flex-1 sm:flex-none px-3 py-2 text-sm text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 min-h-[44px]"
          >
            {t('common.refresh')}
          </button>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex-1 sm:flex-none px-3 py-2 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 min-h-[44px]"
          >
            {showAddForm ? t('common.cancel') : t('customer.addCustomer')}
          </button>
        </div>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <form onSubmit={handleAddCustomer} className="p-3 sm:p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {COLUMNS.map(col => (
              <div key={col.key}>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">{t(col.labelKey)}</label>
                <input
                  type="text"
                  value={newCustomer[col.key as keyof typeof newCustomer]}
                  onChange={e => setNewCustomer({ ...newCustomer, [col.key]: e.target.value })}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder={t(col.labelKey)}
                />
              </div>
            ))}
          </div>
          <div className="mt-4 flex justify-end">
            <button
              type="submit"
              disabled={createCustomer.isLoading}
              className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {createCustomer.isLoading ? t('common.adding') : t('customer.addCustomer')}
            </button>
          </div>
        </form>
      )}

      {/* Mobile Card View */}
      <div className="lg:hidden space-y-3">
        {customers?.map((customer: ICustomer) => (
          <MobileCard
            key={customer.row_index}
            title={customer.chinese_name || t('customer.unnamed')}
            subtitle={customer.customer_id}
            status={
              customer.status ? (
                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                  {customer.status}
                </span>
              ) : undefined
            }
            fields={[
              { label: t('customer.columns.contact1'), value: customer.contact1 || '-' },
              { label: t('customer.columns.phone'), value: customer.phone || '-' },
              { label: t('customer.columns.email1'), value: customer.email1 || '-' },
              { label: t('customer.columns.mainProduct'), value: customer.main_product || '-' },
            ]}
            actions={[
              { label: t('common.delete'), onClick: () => handleDeleteCustomer(customer.row_index), variant: 'danger' as const },
            ]}
          />
        ))}
        {(!customers || customers.length === 0) && (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">{t('customer.noData')}</div>
        )}
        <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
          {t('customer.editOnDesktop', 'Switch to desktop view for inline editing')}
        </p>
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto overflow-y-auto max-h-[600px] border border-gray-200 dark:border-gray-700 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0 z-10">
            <tr>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-10">
                {t('common.row')}
              </th>
              {COLUMNS.map(col => (
                <th
                  key={col.key}
                  className={`px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${col.width}`}
                >
                  {t(col.labelKey)}
                  <span className="text-gray-400 dark:text-gray-500 ml-1">({col.column})</span>
                </th>
              ))}
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-16">
                {t('common.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {customers?.map((customer: ICustomer) => (
              <tr key={customer.row_index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-2 py-1 text-xs text-gray-500 dark:text-gray-400">{customer.row_index}</td>
                {COLUMNS.map(col => (
                  <td key={col.key} className={`px-1 py-1 ${col.width}`}>
                    <EditableCell
                      value={customer[col.key as keyof ICustomer]?.toString() || ''}
                      rowIndex={customer.row_index}
                      column={col.column}
                      onUpdate={handleCellUpdate}
                      isUpdating={updatingCells.has(`${customer.row_index}-${col.column}`)}
                    />
                  </td>
                ))}
                <td className="px-2 py-1">
                  <button
                    onClick={() => handleDeleteCustomer(customer.row_index)}
                    className="text-red-600 hover:text-red-800 text-xs"
                    title={t('common.delete')}
                  >
                    {t('common.delete')}
                  </button>
                </td>
              </tr>
            ))}
            {(!customers || customers.length === 0) && (
              <tr>
                <td colSpan={COLUMNS.length + 2} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                  {t('customer.noData')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Status */}
      {updatingCells.size > 0 && (
        <div className="fixed bottom-4 right-4 bg-yellow-100 text-yellow-800 px-4 py-2 rounded-lg shadow-lg">
          {t('customer.saving')}
        </div>
      )}
    </div>
  );
};

export default CustomerList;
